import { digger } from '@cbre/digger';
import { concatValuesBy, defaultTxt } from '@miq/utils';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ReportRecord } from '../../../models/report.model';
import { LovValues, TemplateFieldsDisplayFn } from '../../../models/template.model';
import { getMediaInformation } from '../templates-formatter.const';
import { sortAttributesByFieldId } from '../utils/argument-sorting';
import {
  displayFnWrapper,
  formatExternalMeasurementFieldVal,
  getLovAttributesFormattedData,
  getMeasuremetsAttributesData,
  mapAndFormatDynamicValue,
  minMaxRangeFormattedValue,
} from '../utils/dynamic-data-retrievers';

export const displayFnMap: TemplateFieldsDisplayFn = {
  27: {
    displayFn: (record: ReportRecord, options, ...args) => {
      const finalArgs = sortAttributesByFieldId(options.locale, args, 27);
      const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
      return concatValuesBy(', ', ...values);
    },
    routeFn: ({ data: entityRecord }: ReportRecord) => {
      return ['/records', 'property', digger(entityRecord, 'entity.id'), 'details'];
    },
  },
  28: {
    displayFn: (record: ReportRecord, _, landlord) => {
      const landlordVal = mapAndFormatDynamicValue(record, landlord);
      return Array.isArray(landlordVal) ? concatValuesBy(', ', ...landlordVal) : landlordVal;
    },
  },
  29: {
    displayFn: (record: ReportRecord, _, ownership) => {
      const result = getLovAttributesFormattedData(record, ownership);
      return result;
    },
  },
  49: {
    displayFn: (record: ReportRecord, _options, arg1, arg2) => {
      const values = concatValuesBy(
        defaultTxt,
        mapAndFormatDynamicValue(record, arg1),
        mapAndFormatDynamicValue(record, arg2)
      );
      return values;
    },
  },
  98: {
    // 98 - Primary Image
    displayFn: (record: ReportRecord) => {
      return record.associatedProperty
        ? record.associatedProperty.getMediaInformation()
        : record.data.getMediaInformation();
    },
  },
  99: {
    // 99 - Additional Image
    displayFn: (record: ReportRecord) => {
      return record.associatedProperty
        ? record.associatedProperty.getMediaInformation()
        : record.data.getMediaInformation();
    },
  },
  100: {
    displayFn: (record: ReportRecord, _, leed) => {
      const dataFormattingDesc = 'LEED';
      leed = {
        ...leed,
        dataFormattingDesc,
      };
      return leed.lovValues.pipe(
        switchMap(lov => {
          const childLov: Observable<LovValues[]> = lov[dataFormattingDesc];
          const result = getMeasuremetsAttributesData(record, leed, childLov);
          return result;
        })
      );
    },
  },
  101: {
    displayFn: (record: ReportRecord, _, green) => {
      const dataFormattingDesc = 'Green';
      green = {
        ...green,
        dataFormattingDesc,
      };
      return green.lovValues.pipe(
        switchMap(lov => {
          const childLov: Observable<LovValues[]> = lov[dataFormattingDesc];
          const result = getMeasuremetsAttributesData(record, green, childLov);
          return result;
        })
      );
    },
  },
  102: {
    displayFn: displayFnWrapper,
  },
  103: {
    displayFn: displayFnWrapper,
  },
  104: {
    displayFn: (record: ReportRecord, _, management) => {
      const managementValue = mapAndFormatDynamicValue(record, management);
      return Array.isArray(managementValue) ? concatValuesBy(', ', ...managementValue) : managementValue;
    },
  },
  105: {
    displayFn: (record: ReportRecord, options, ...args) => {
      // order of fields requested in MIQ-7646
      const finalArgs = sortAttributesByFieldId(options.locale, args, 105);
      const metroValues = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
      return concatValuesBy(', ', ...metroValues);
    },
  },
  106: {
    displayFn: (record: ReportRecord, _options, raisedFloor) => {
      raisedFloor = {
        ...raisedFloor,
        dataFormattingDesc: 'raisedflrdepth',
      };
      const raisedFloorValue = formatExternalMeasurementFieldVal(record, raisedFloor, 'property_measurement_size');
      return raisedFloorValue;
    },
  },
  107: {
    displayFn: (record: ReportRecord, _options, arg1, maxTypicalFloorPlate) => {
      const minTypicalFloorPlate = { ...arg1, uomId: null, uomArgument: null };
      return minMaxRangeFormattedValue(record, minTypicalFloorPlate, maxTypicalFloorPlate);
    },
  },
  108: {
    displayFn: (record: ReportRecord, _options, ceiling) => {
      ceiling = {
        ...ceiling,
        dataFormattingDesc: 'ceiling',
      };
      const ceilingValue = formatExternalMeasurementFieldVal(record, ceiling, 'property_measurement_size');
      return ceilingValue;
    },
  },
  109: {
    displayFn: displayFnWrapper,
  },
  110: {
    displayFn: displayFnWrapper,
  },
  111: {
    displayFn: displayFnWrapper,
  },
  112: {
    displayFn: (record: ReportRecord, options, weekEndStart, weekDaysStart, weekEndEnd, weekDaysEnd) => {
      const weekDays = `${options.jsonData.recordPPT.weekend} ${concatValuesBy(
        ' - ',
        mapAndFormatDynamicValue(record, weekDaysStart),
        mapAndFormatDynamicValue(record, weekDaysEnd)
      )}`;
      const weekEnd = `${options.jsonData.recordPPT.saturday} ${concatValuesBy(
        ' - ',
        mapAndFormatDynamicValue(record, weekEndStart),
        mapAndFormatDynamicValue(record, weekEndEnd)
      )}`;
      return concatValuesBy(' \r', weekDays, weekEnd);
    },
  },
  113: {
    displayFn: displayFnWrapper,
  },
  114: {
    displayFn: (record: ReportRecord, options, carParkingFeeMonth, carParkingFeeLot) => {
      const floatingVal = `${options.jsonData.recordPPT.floating} ${
        options.jsonData.recordPPT.rmb
      } ${mapAndFormatDynamicValue(record, carParkingFeeLot)}`;
      const fixedVal = `${options.jsonData.recordPPT.fixed} ${
        options.jsonData.recordPPT.rmb
      } ${mapAndFormatDynamicValue(record, carParkingFeeMonth)}`;
      return concatValuesBy(' \r', floatingVal, fixedVal);
    },
  },
  115: {
    displayFn: displayFnWrapper,
  },
  116: {
    displayFn: displayFnWrapper,
  },
  117: {
    displayFn: displayFnWrapper,
  },
  118: {
    displayFn: displayFnWrapper,
  },
  119: {
    displayFn: displayFnWrapper,
  },
  120: {
    displayFn: displayFnWrapper,
  },
  121: {
    displayFn: displayFnWrapper,
  },
  122: {
    displayFn: displayFnWrapper,
  },
  123: {
    displayFn: displayFnWrapper,
  },
  347: {
    // 347 - Floor Plan Images
    displayFn: (record: ReportRecord) => {
      return getMediaInformation(record);
    },
  },
  335: {
    // 335 - Flor and Suite info
    displayFn: (record: ReportRecord, _, suiteArg, floorArg) => {
      const floorValue = mapAndFormatDynamicValue(record, floorArg);
      const suiteValue = mapAndFormatDynamicValue(record, suiteArg);
      return `${floorValue} | ${suiteValue}`;
    },
  },
};
